/**
 * Build a slide full-size image URL.
 *
 * ``/api/slide-sense/slides/{id}/image`` streams the PNG through the
 * backend, so a plain `<img src>` works directly and the browser's
 * HTTP cache handles dedupe. ``onImgError`` lets callers swap to a
 * fallback when the backend returns 204 (render failed) or 404.
 */

import { useState } from "react"

const IMAGE_BASE = "/api/slide-sense/slides"

function buildImageUrl(slideId: string | undefined): string | null {
  if (!slideId) return null
  return `${IMAGE_BASE}/${slideId}/image`
}

export function useSlideImage(slideId: string | undefined) {
  const url = buildImageUrl(slideId)
  const [error, setError] = useState(false)
  const onImgError = () => setError(true)
  return { url, error, onImgError }
}

/**
 * Imperatively resolve a slide's image URL — kept for preload paths
 * that want a string without mounting a hook. Always returns the
 * route URL when ``slideId`` is set; the caller is responsible for
 * handling 204/404 at fetch time.
 */
export function resolveSlideImageUrl(slideId: string): string | null {
  return buildImageUrl(slideId)
}

/** No-op kept for back-compat with existing test setups; the browser HTTP cache now handles dedupe. */
export function clearSlideImageCache(): void {}
